/**
 * How the school runs Chromebook location: what it collects and when, who is covered, how long it's kept, the areas
 * that matter, the access point map, and the policy the Chromebooks are given in Google Admin.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCopy, MapPin, Plus, Trash2, Wifi } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AddOnRequired } from '../../components/plan';
import { useToast } from '../../components/toast';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  PageHeader,
  PageLoader,
  Select,
  Switch,
  Table,
  Td,
  Textarea,
  Th,
} from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { usePlan } from '../../lib/billing';
import { COLLECTION_LABEL, GEOFENCE_LABEL, useAccessPoints, useGeofences, useLocationSettings, type Geofence, type LocationSettings } from '../../lib/device-location';
import { formatDateTime } from '../../lib/format';
import { useBuildings, useRooms } from '../../lib/hooks';
import type { WeeklyHours } from '../../lib/types';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function LocationSettingsPage() {
  const plan = usePlan();
  const { can } = useAuth();
  const allowed = plan.has('DEVICE_LOCATION');
  const settings = useLocationSettings(allowed && can('ADMIN'));

  if (!allowed) {
    return (
      <AddOnRequired
        feature="DEVICE_LOCATION"
        description="See where the school's Chromebooks are, place them indoors from your access points, draw the campus and hear when a device leaves it, and put a device into missing mode until it's found."
      />
    );
  }
  if (!can('ADMIN')) return <ErrorState error={new Error('School administrators set this up.')} />;
  if (settings.isLoading) return <PageLoader />;
  if (settings.error || !settings.data) return <ErrorState error={settings.error} onRetry={() => settings.refetch()} />;

  return (
    <>
      <PageHeader title="Chromebook location" description="What the school collects, who it covers, and how long it's kept." back={{ to: '/device-location', label: 'Fleet map' }} />
      <div className="space-y-6">
        <PolicyCard settings={settings.data} />
        <EnrollmentCard settings={settings.data} />
        <AreasCard />
        <AccessPointsCard />
      </div>
    </>
  );
}

/** What's collected, when, and for how long. */
function PolicyCard({ settings }: { settings: LocationSettings }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(settings);
  const [hours, setHours] = useState<WeeklyHours>(settings.schoolHours ?? settings.defaultSchoolHours);
  useEffect(() => {
    setForm(settings);
    setHours(settings.schoolHours ?? settings.defaultSchoolHours);
  }, [settings]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/device-location/settings', {
        enabled: form.enabled,
        collection: form.collection,
        intervalMinutes: form.intervalMinutes,
        missingIntervalMinutes: form.missingIntervalMinutes,
        retentionDays: form.retentionDays,
        quietAfterDays: form.quietAfterDays,
        schoolHours: hours,
        orgUnits: form.orgUnits,
        excludedOrgUnits: form.excludedOrgUnits,
        consentRequired: form.consentRequired,
        consentVersion: form.consentVersion ?? undefined,
        lockMessage: form.lockMessage ?? undefined,
        alertTickets: form.alertTickets,
        alertDepartmentId: form.alertDepartmentId,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['device-location'] });
      toast.success('Saved');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const setDay = (day: number, range: [string, string] | null) =>
    setHours((current) => ({ ...current, [String(day)]: range ? [range] : [] }));
  const lines = (values: string[]) => values.join('\n');
  const paths = (value: string) => value.split('\n').map((line) => line.trim()).filter(Boolean);

  return (
    <Card>
      <CardHeader
        title="What the school collects"
        description="Student privacy law is the reason for the school-day default: widen it only if your policy says so."
        actions={
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        }
      />
      <div className="space-y-5 p-5">
        <Switch
          checked={form.enabled}
          onChange={(enabled) => setForm({ ...form, enabled })}
          label="Collect Chromebook locations"
          description="Off: nothing is collected and the extension stays quiet, whatever is installed."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="When" hint="Devices flagged missing always report, whatever this says.">
            <Select value={form.collection} onChange={(event) => setForm({ ...form, collection: event.target.value as LocationSettings['collection'] })}>
              {Object.entries(COLLECTION_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="How often, in minutes">
            <Input type="number" min={5} max={240} value={form.intervalMinutes} onChange={(event) => setForm({ ...form, intervalMinutes: Number(event.target.value) })} />
          </Field>
          <Field label="How often once flagged missing, in minutes">
            <Input type="number" min={5} max={240} value={form.missingIntervalMinutes} onChange={(event) => setForm({ ...form, missingIntervalMinutes: Number(event.target.value) })} />
          </Field>
          <Field label="Keep location history for, in days" hint="Older points are deleted every night.">
            <Input type="number" min={1} max={400} value={form.retentionDays} onChange={(event) => setForm({ ...form, retentionDays: Number(event.target.value) })} />
          </Field>
          <Field label="Call a device quiet after, in days">
            <Input type="number" min={1} max={60} value={form.quietAfterDays} onChange={(event) => setForm({ ...form, quietAfterDays: Number(event.target.value) })} />
          </Field>
          <Field label="Message on a flagged device">
            <Textarea rows={2} maxLength={300} value={form.lockMessage ?? ''} onChange={(event) => setForm({ ...form, lockMessage: event.target.value })} placeholder="This Chromebook belongs to… Please return it to…" />
          </Field>
        </div>

        {form.collection === 'SCHOOL_HOURS' && (
          <div>
            <p className="mb-2 text-sm font-medium text-slate-900">The school day</p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {[1, 2, 3, 4, 5, 6, 0].map((day) => {
                const range = hours[String(day)]?.[0] ?? null;
                return (
                  <li key={day} className="flex flex-wrap items-center gap-3 px-4 py-2">
                    <div className="w-36">
                      <Switch checked={!!range} onChange={(open) => setDay(day, open ? ['07:30', '16:00'] : null)} label={WEEKDAYS[day]} />
                    </div>
                    {range ? (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Input type="time" value={range[0]} onChange={(event) => setDay(day, [event.target.value, range[1]])} className="w-32" aria-label={`${WEEKDAYS[day]} opens`} />
                        <span>to</span>
                        <Input type="time" value={range[1]} onChange={(event) => setDay(day, [range[0], event.target.value])} className="w-32" aria-label={`${WEEKDAYS[day]} closes`} />
                      </div>
                    ) : (
                      <span className="text-sm text-slate-400">Nothing collected</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Org units this covers" hint="One path per line, e.g. /Students/Middle School. Empty: all of them.">
            <Textarea rows={3} value={lines(form.orgUnits)} onChange={(event) => setForm({ ...form, orgUnits: paths(event.target.value) })} placeholder="/Students" />
          </Field>
          <Field label="Org units left out" hint="Takes precedence, for populations you don't track.">
            <Textarea rows={3} value={lines(form.excludedOrgUnits)} onChange={(event) => setForm({ ...form, excludedOrgUnits: paths(event.target.value) })} placeholder="/Staff" />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Switch
            checked={form.alertTickets}
            onChange={(alertTickets) => setForm({ ...form, alertTickets })}
            label="Open a ticket for alerts"
            description="A device off campus, in a restricted area, or quiet for days opens a technology ticket."
          />
          <Field label="Open those tickets in">
            <Select value={form.alertDepartmentId ?? ''} onChange={(event) => setForm({ ...form, alertDepartmentId: event.target.value || null })}>
              <option value="">No department</option>
              {settings.departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </Select>
          </Field>
          <Switch
            checked={form.consentRequired}
            onChange={(consentRequired) => setForm({ ...form, consentRequired })}
            label="Track acknowledgement of the school's notice"
            description="Record who has acknowledged the school's device policy."
          />
          <Field label="Notice version">
            <Input value={form.consentVersion ?? ''} maxLength={60} onChange={(event) => setForm({ ...form, consentVersion: event.target.value })} placeholder="2026-27 AUP" />
          </Field>
        </div>
      </div>
    </Card>
  );
}

/** The policy Google Admin needs, with the school's enrollment token in it. */
function EnrollmentCard({ settings }: { settings: LocationSettings }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const origin = window.location.origin;
  const school = window.location.pathname.split('/').filter(Boolean)[0] ?? '';

  const make = useMutation({
    mutationFn: () => api.post<{ token: string }>('/device-location/enrollment-token'),
    onSuccess: ({ token: created }) => {
      setToken(created);
      setConfirm(false);
      void queryClient.invalidateQueries({ queryKey: ['device-location', 'settings'] });
      toast.success('New token made. Copy it now: it isn’t shown again.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const policy = JSON.stringify(
    {
      serverUrl: { Value: `${origin}/${school}` },
      enrollmentToken: { Value: token ?? 'paste-the-token-here' },
      contactMessage: { Value: settings.lockMessage ?? 'This Chromebook belongs to the school. Please return it to the office.' },
    },
    null,
    2,
  );

  return (
    <Card>
      <CardHeader
        title="The Chromebooks"
        description="Force-install the extension in Google Admin, then give it this policy. Students see no prompts."
        actions={
          <Button onClick={() => setConfirm(true)} loading={make.isPending}>
            {settings.enrolled ? 'New token' : 'Make a token'}
          </Button>
        }
      />
      <div className="space-y-4 p-5 text-sm">
        <ol className="list-decimal space-y-1 pl-5 text-slate-600">
          <li>In Google Admin: Devices → Chrome → Apps &amp; extensions → Users &amp; browsers.</li>
          <li>Add the Edvora Campus Asset location extension and set it to <strong>Force install</strong> for the org units you cover.</li>
          <li>Paste the policy below into <strong>Policy for extensions</strong>.</li>
        </ol>
        {token && (
          <Alert tone="amber" title="Copy this token now">
            It isn’t shown again. Making another one stops Chromebooks that haven’t enrolled yet from using the old one.
          </Alert>
        )}
        <div className="relative">
          <pre className="overflow-x-auto rounded-lg bg-slate-900 p-4 text-xs text-slate-100">{policy}</pre>
          <Button
            size="sm"
            className="absolute right-2 top-2"
            icon={<ClipboardCopy />}
            onClick={() => {
              void navigator.clipboard.writeText(policy);
              toast.success('Policy copied');
            }}
          >
            Copy
          </Button>
        </div>
        <p className="text-slate-500">
          {settings.enrollmentTokenSetAt ? `Token made ${formatDateTime(settings.enrollmentTokenSetAt)}.` : 'No token yet.'} Only its hash is stored.
        </p>
      </div>
      <ConfirmDialog
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={() => make.mutate()}
        loading={make.isPending}
        title="Make a new enrollment token?"
        description="Chromebooks already enrolled keep reporting. Any that haven't enrolled yet need the new token in their policy."
        confirmLabel="Make a token"
      />
    </Card>
  );
}

/** Campus grounds and the places devices may or may not be. */
function AreasCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const fences = useGeofences();
  const [form, setForm] = useState({ name: '', kind: 'CAMPUS', buildingId: '', centerLat: '', centerLng: '', radiusM: '400' });
  const [removing, setRemoving] = useState<Geofence | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['device-location'] });
  const add = useMutation({
    mutationFn: () =>
      api.post('/device-location/geofences', {
        name: form.name,
        kind: form.kind,
        buildingId: form.buildingId || null,
        centerLat: Number(form.centerLat),
        centerLng: Number(form.centerLng),
        radiusM: Number(form.radiusM),
        alertOnExit: form.kind === 'CAMPUS',
        alertOnEnter: form.kind === 'RESTRICTED',
      }),
    onSuccess: () => {
      void refresh();
      setForm({ name: '', kind: 'CAMPUS', buildingId: '', centerLat: '', centerLng: '', radiusM: '400' });
      toast.success('Area added');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const remove = useMutation({
    mutationFn: (fence: Geofence) => api.delete(`/device-location/geofences/${fence.id}`),
    onSuccess: () => {
      void refresh();
      setRemoving(null);
      toast.success('Area deleted');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader title="Areas" description="School grounds raise an alert when a device leaves. Restricted areas raise one when a device turns up inside." />
      <form
        className="grid gap-3 border-b border-slate-100 p-5 sm:grid-cols-[1.4fr_1fr_1fr_1fr_1fr_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <Field label="Name">
          <Input required maxLength={120} value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Main campus" />
        </Field>
        <Field label="Kind">
          <Select value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })}>
            {Object.entries(GEOFENCE_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Centre latitude">
          <Input required value={form.centerLat} onChange={(event) => setForm({ ...form, centerLat: event.target.value })} placeholder="41.8781" />
        </Field>
        <Field label="Centre longitude">
          <Input required value={form.centerLng} onChange={(event) => setForm({ ...form, centerLng: event.target.value })} placeholder="-87.6298" />
        </Field>
        <Field label="Radius, metres">
          <Input required type="number" min={25} max={50000} value={form.radiusM} onChange={(event) => setForm({ ...form, radiusM: event.target.value })} />
        </Field>
        <Button type="submit" variant="primary" icon={<Plus />} loading={add.isPending}>
          Add
        </Button>
      </form>
      {(fences.data ?? []).length === 0 ? (
        <EmptyState icon={<MapPin />} title="No areas yet" description="Add the school grounds so a device leaving them raises an alert." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Name</Th>
              <Th>Kind</Th>
              <Th>Building</Th>
              <Th>Where</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(fences.data ?? []).map((fence) => (
              <tr key={fence.id}>
                <Td>{fence.name}</Td>
                <Td>
                  <Badge tone={fence.kind === 'RESTRICTED' ? 'red' : fence.kind === 'ALLOWED' ? 'green' : 'brand'}>{GEOFENCE_LABEL[fence.kind]}</Badge>
                </Td>
                <Td>{fence.building?.name ?? '—'}</Td>
                <Td className="text-slate-500">
                  {fence.polygon?.length
                    ? `${fence.polygon.length} corners`
                    : `${fence.centerLat?.toFixed(5)}, ${fence.centerLng?.toFixed(5)} · ${fence.radiusM} m`}
                </Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => setRemoving(fence)}>
                    Delete
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      <ConfirmDialog
        open={!!removing}
        onClose={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing)}
        loading={remove.isPending}
        tone="danger"
        title={`Delete ${removing?.name ?? 'this area'}?`}
        description="Devices stop being compared with it. Points already recorded are unchanged."
        confirmLabel="Delete"
      />
    </Card>
  );
}

/** The access point map that places devices indoors. */
function AccessPointsCard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const points = useAccessPoints();
  const buildings = useBuildings();
  const rooms = useRooms();
  const [form, setForm] = useState({ bssid: '', ssid: '', buildingId: '', roomId: '', label: '' });
  const [paste, setPaste] = useState('');

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['device-location', 'access-points'] });
  const add = useMutation({
    mutationFn: () =>
      api.post('/device-location/access-points', {
        bssid: form.bssid,
        ssid: form.ssid || undefined,
        label: form.label || undefined,
        buildingId: form.buildingId || null,
        roomId: form.roomId || null,
      }),
    onSuccess: () => {
      void refresh();
      setForm({ bssid: '', ssid: '', buildingId: '', roomId: '', label: '' });
      toast.success('Access point mapped');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  /** One access point per line: BSSID, room name, SSID. */
  const importRows = useMutation({
    mutationFn: () => {
      const roomsByName = new Map((rooms.data ?? []).map((room) => [room.name.toLowerCase(), room]));
      const rowsIn = paste
        .split('\n')
        .map((line) => line.split(/[,\t]/).map((cell) => cell.trim()))
        .filter((cells) => cells[0]);
      const rows = rowsIn.map(([bssid, roomName, ssid]) => {
        const room = roomName ? roomsByName.get(roomName.toLowerCase()) : undefined;
        return { bssid: bssid.toLowerCase(), ssid: ssid || undefined, roomId: room?.id ?? null, buildingId: room?.buildingId ?? null, label: roomName || undefined };
      });
      return api.post<{ added: number; updated: number }>('/device-location/access-points/import', { rows });
    },
    onSuccess: ({ added, updated }) => {
      void refresh();
      setPaste('');
      toast.success(`Mapped ${added} new and updated ${updated} access point${added + updated === 1 ? '' : 's'}`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/device-location/access-points/${id}`),
    onSuccess: () => {
      void refresh();
      toast.success('Removed');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <Card>
      <CardHeader title="Access points" description="Match the access point a Chromebook is on to a building and room, for where GPS can't reach." />
      <form
        className="grid gap-3 border-b border-slate-100 p-5 sm:grid-cols-[1.2fr_1fr_1fr_1fr_auto] sm:items-end"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <Field label="BSSID">
          <Input required value={form.bssid} onChange={(event) => setForm({ ...form, bssid: event.target.value })} placeholder="00:11:22:33:44:55" />
        </Field>
        <Field label="Network name">
          <Input value={form.ssid} onChange={(event) => setForm({ ...form, ssid: event.target.value })} placeholder="Campus-WiFi" />
        </Field>
        <Field label="Building">
          <Select
            value={form.buildingId}
            onChange={(event) => setForm({ ...form, buildingId: event.target.value, roomId: '' })}
          >
            <option value="">No building</option>
            {(buildings.data ?? []).map((building) => (
              <option key={building.id} value={building.id}>
                {building.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Room">
          <Select value={form.roomId} onChange={(event) => setForm({ ...form, roomId: event.target.value })}>
            <option value="">No room</option>
            {(rooms.data ?? []).filter((room) => !form.buildingId || room.buildingId === form.buildingId).map((room) => (
              <option key={room.id} value={room.id}>
                {room.name}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" variant="primary" icon={<Plus />} loading={add.isPending}>
          Map
        </Button>
      </form>

      <div className="grid gap-3 border-b border-slate-100 p-5 sm:grid-cols-[1fr_auto] sm:items-end">
        <Field label="Or paste your controller's export" hint="One per line: BSSID, room name, network name.">
          <Textarea rows={3} value={paste} onChange={(event) => setPaste(event.target.value)} placeholder="00:11:22:33:44:55, Room 101, Campus-WiFi" />
        </Field>
        <Button loading={importRows.isPending} disabled={!paste.trim()} onClick={() => importRows.mutate()}>
          Import
        </Button>
      </div>

      {(points.data ?? []).length === 0 ? (
        <EmptyState icon={<Wifi />} title="No access points mapped" description="Without these, devices are placed by their location fix alone." />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>BSSID</Th>
              <Th>Network</Th>
              <Th>Where</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(points.data ?? []).map((access) => (
              <tr key={access.id}>
                <Td className="font-mono text-xs">{access.bssid}</Td>
                <Td>{access.ssid ?? '—'}</Td>
                <Td>{[access.room?.name, access.building?.name].filter(Boolean).join(' · ') || access.label || '—'}</Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" icon={<Trash2 />} onClick={() => remove.mutate(access.id)}>
                    Remove
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}
